import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, Loader2, Plus, Trash2, Calendar } from 'lucide-react';
import { DatePicker, TimePicker } from '../DateTimePicker';
import { getWorkersDropdown } from '../../services/api';

export function SiteVisitModal({ isOpen, onClose, onSubmit, loading, quote }) {
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [remarks, setRemarks] = useState('');
  const [assignees, setAssignees] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState('');

  const { data: workers } = useQuery({
    queryKey: ['workersDropdown'],
    queryFn: () => getWorkersDropdown().then(res => res.data),
    enabled: isOpen
  });

  const userTypeLabel = { 0: 'Admin', 1: 'Inhouse', 2: 'Contractor', 3: 'SubContractor', 4: 'Employee' };

  const handleAddAssignee = (workerId) => {
    if (!workerId) return;
    if (assignees.find(a => a.assignee_id === workerId)) { toast.error('Already assigned'); setSelectedWorker(''); return; }
    const worker = workers?.find(w => w.id === workerId);
    setAssignees([...assignees, {
      assignee_id: workerId,
      name: worker?.name || '',
      user_type: worker?.user_type,
      start_time: '',
      end_time: ''
    }]);
    setSelectedWorker('');
  };

  const updateAssignee = (idx, field, value) => {
    const updated = [...assignees];
    updated[idx] = { ...updated[idx], [field]: value };
    setAssignees(updated);
  };

  const removeAssignee = (idx) => {
    setAssignees(assignees.filter((_, i) => i !== idx));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!dateStart) { toast.error('Please select a start date'); return; }
    if (assignees.length === 0) { toast.error('Please add at least one assignee'); return; }
    onSubmit({
      start_date: dateStart,
      end_date: dateEnd || dateStart,
      remarks,
      assignees: assignees.map(a => ({
        assignee_id: a.assignee_id,
        start_time: a.start_time,
        end_time: a.end_time
      }))
    });
  };

  if (!isOpen) return null;

  const availableWorkers = workers?.filter(w => !assignees.find(a => a.assignee_id === w.id)) || [];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="font-semibold">Assign Site Visit Schedule</h2>
          <button onClick={onClose} data-testid="close-site-visit-modal"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Start Date *</label>
              <DatePicker value={dateStart} onChange={(v) => setDateStart(v)} testId="site-visit-start-date" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">End Date</label>
              <DatePicker value={dateEnd} onChange={(v) => setDateEnd(v)} testId="site-visit-end-date" />
            </div>
          </div>

          {/* Assignee Selection */}
          <div>
            <label className="block text-sm font-medium mb-1">Assign To *</label>
            <select
              value={selectedWorker}
              onChange={(e) => handleAddAssignee(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
              data-testid="site-visit-assignee-select"
            >
              <option value="">Select person to assign...</option>
              {availableWorkers.map(w => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </div>

          {/* Assigned Workers with Time */}
          {assignees.length > 0 && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-slate-700">Assigned Staff</label>
              {assignees.map((a, idx) => (
                <div key={a.assignee_id} className="p-3 border border-slate-200 rounded-lg bg-slate-50" data-testid={`assignee-row-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-primary rounded-full flex items-center justify-center text-white text-xs font-medium">{a.name.charAt(0)}</div>
                      <div>
                        <span className="text-sm font-medium">{a.name}</span>
                        <span className="text-xs text-slate-500 ml-1.5">({userTypeLabel[a.user_type] || 'Worker'})</span>
                      </div>
                    </div>
                    <button type="button" onClick={() => removeAssignee(idx)} className="p-1 text-slate-400 hover:text-red-500" data-testid={`remove-assignee-${idx}`}>
                      <X size={16} />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Start Time</label>
                      <TimePicker value={a.start_time} onChange={(v) => updateAssignee(idx, 'start_time', v)} testId={`assignee-start-time-${idx}`} />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">End Time</label>
                      <TimePicker value={a.end_time} onChange={(v) => updateAssignee(idx, 'end_time', v)} testId={`assignee-end-time-${idx}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Remarks (posted as comment for employees to read) */}
          <div>
            <label className="block text-sm font-medium mb-1">Remarks <span className="text-xs text-slate-400 font-normal">(visible as a comment to assigned employees)</span></label>
            <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" rows={3} placeholder="Add instructions or notes for the assigned staff..." data-testid="site-visit-remarks" />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded">Cancel</button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm bg-green-500 text-white rounded flex items-center gap-2" data-testid="save-site-visit-btn">
              {loading && <Loader2 size={16} className="animate-spin" />}
              Save Schedule
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Confirmation Modal
